import { reportEvent } from '@/lib/monitoring'
import { ServiceError } from '../errors'
import type { Stay } from '../reserva/types'
import { CIVITATIS_DESTINATION_BY_PROVINCE } from './civitatis-provinces'

/**
 * Experiencias de partner: widget de Civitatis (D14, actualizada en la fase 4 con D55). Mismo disparador y misma
 * petición que la app actual (`src/libs/civitatis/components/ExperiencesIframe.tsx` y `Home.tsx:40,135`):
 * - solo para los entornos de `VITE_CIVITATIS_ENVIRONMENTS` (hoy, Renthas);
 * - destino = `civitatisId` de la reserva o, si falta, el de su provincia;
 * - GET al widget con el afiliado 103221, 6 actividades, idioma de la app y EUR.
 * Devuelve el HTML tal cual: la pantalla lo sanea con lista blanca (`lib/partnerWidget.tsx`).
 */
export type PartnerStay = Pick<Stay, 'guestCode' | 'environmentId' | 'partner'>
export type PartnerProvider = 'civitatis'

const LANG: Record<string, string> = { es: 'es', en: 'en', fr: 'fr', it: 'it' }

export const civitatisEnvironments = (value = import.meta.env.VITE_CIVITATIS_ENVIRONMENTS): string[] =>
  (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

/** Destino de Civitatis o null si la reserva no debe verlo (otro entorno o sin destino), como la app actual. */
export function civitatisDestination(stay: PartnerStay, environments = civitatisEnvironments()): string | null {
  if (!environments.includes(stay.environmentId)) return null
  return stay.partner.civitatisId ?? (stay.partner.provinceId ? (CIVITATIS_DESTINATION_BY_PROVINCE[stay.partner.provinceId] ?? null) : null)
}

/** URL del widget, con la misma query que la app actual. */
export const civitatisWidgetUrl = (destination: string, lang: string) =>
  `https://www.civitatis.com/widget-activities/?affiliated=103221&display=cosy&cant=6&lang=${LANG[lang] ?? 'es'}&currency=EUR&destination=${destination}&transfer=0&cmp=Widget_Malaga_ES&width=100%&hideButton=0&centerContent=1&typeSelection=all&color=0749f7&typography=Montserrat&removeBackground=1&showShadow=1&roundedButtons=1`

export async function fetchCivitatisWidget(destination: string, lang: string, signal?: AbortSignal): Promise<string> {
  let response: Response
  try {
    response = await fetch(civitatisWidgetUrl(destination, lang), signal ? { signal } : {})
  } catch (e) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new ServiceError('offline')
    throw new ServiceError('server', e instanceof Error ? e.message : String(e))
  }
  if (!response.ok) {
    reportEvent('http_error', { endpoint: 'civitatis-widget-activities', status: response.status })
    throw new ServiceError('server', `HTTP ${response.status}`, response.status)
  }
  return response.text()
}
